/**
 * NIZAM ONE ERP — Permission Key Registry
 *
 * Every key in this file exists in rbac_permissions.action (270 permissions).
 * Keys are derived directly from the DB — no invented keys.
 *
 * Usage:
 *   import { PERMISSIONS } from '@/lib/permissions/permissionKeys';
 *   const canPost = await hasPermission(PERMISSIONS.FINANCE.POST_JOURNAL, companyId);
 *
 * HIGH-RISK permissions are marked with @high-risk comments.
 * Never grant high-risk permissions without explicit CFO/CTO/CHRO sign-off.
 */

// ── SYSTEM MODULE ─────────────────────────────────────────────────────────────
export const SYSTEM = {
  /** Invite new users by email */
  USERS_INVITE:        'users.invite',
  /** Manage invitations (resend, cancel, view log) */
  USERS_INVITE_MANAGE: 'users.invite_manage',
  /** @high-risk View audit trail events */
  AUDIT_VIEW:          'system.audit.view',
  /** @high-risk Modify system configuration */
  CONFIG_MANAGE:       'system.config.manage',
  DASHBOARD_VIEW:      'system.dashboard.view',
  /** @high-risk Manage external integrations */
  INTEGRATIONS_MANAGE: 'system.integrations.manage',
  MONITORING_VIEW:     'system.monitoring.view',
  /** @high-risk Manage roles and permissions */
  RBAC_MANAGE:         'system.rbac.manage',
  /** @high-risk Manage user accounts */
  USERS_MANAGE:        'system.users.manage',
  USERS_VIEW:          'system.users.view',
} as const;

// ── ADMIN MODULE (legacy) ─────────────────────────────────────────────────────
export const ADMIN = {
  /** @high-risk @legacy Use system.rbac.manage instead */
  MANAGE_RBAC:    'manage_rbac',
  MANAGE_SETTINGS:'manage_settings',
  MANAGE_USERS:   'manage_users',
  VIEW_AUDIT_LOG: 'view_audit_log',
  VIEW_REPORTS:   'view_reports',
  MESSAGING_MANAGE: 'admin.messaging.manage',
} as const;

// ── FINANCE MODULE ────────────────────────────────────────────────────────────
export const FINANCE = {
  BANK_MANAGE:        'finance.bank.manage',
  BANK_VIEW:          'finance.bank.view',
  BUDGET_MANAGE:      'finance.budget.manage',
  BUDGET_VIEW:        'finance.budget.view',
  COA_MANAGE:         'finance.coa.manage',
  COA_VIEW:           'finance.coa.view',
  CONSOLIDATION_VIEW: 'finance.consolidation.view',
  DASHBOARD_VIEW:     'finance.dashboard.view',
  EPM_VIEW:           'finance.epm.view',
  EXPENSES_APPROVE:   'finance.expenses.approve',
  INTERCOMPANY_MANAGE:'finance.intercompany.manage',
  INTERCOMPANY_VIEW:  'finance.intercompany.view',
  INVOICES_AP_CREATE: 'finance.invoices.ap.create',
  INVOICES_AP_VIEW:   'finance.invoices.ap.view',
  /** @high-risk */
  INVOICES_APPROVE:   'finance.invoices.approve',
  INVOICES_AR_CREATE: 'finance.invoices.ar.create',
  INVOICES_AR_VIEW:   'finance.invoices.ar.view',
  JOURNAL_CREATE:     'finance.journal.create',
  /** @high-risk Post journal entries to GL */
  JOURNAL_POST:       'finance.journal.post',
  JOURNAL_VIEW:       'finance.journal.view',
  LEDGER_VIEW:        'finance.ledger.view',
  /** @high-risk Approve payment vouchers */
  PAYMENTS_APPROVE:   'finance.payments.approve',
  STATEMENTS_VIEW:    'finance.statements.view',
  VAT_MANAGE:         'finance.vat.manage',
  VAT_VIEW:           'finance.vat.view',
  /** @high-risk Close fiscal period */
  PERIOD_CLOSE:       'finance.period.close',
  CONFIGURE:          'finance.configure',
  REPORTS_VIEW:       'reports.finance.view',
  REPORTS_EXPORT:     'reports.finance.export',
  ZATCA_VIEW:         'zatca.view',
  ZATCA_MANAGE:       'zatca.manage',
  // Navigation-aligned canonical keys (PERM-001 / PERM-003 catch-up)
  FINANCE_ZATCA_VIEW:        'finance.zatca.view',
  FINANCE_ZATCA_MANAGE:      'finance.zatca.manage',
  COLLECTION_VIEW:           'finance.collection.view',
  COLLECTION_MANAGE:         'finance.collection.manage',
  // IAS / IFRS disclosure modules — defined in W1B/W1C/W1D/W1E migrations
  PROVISIONS_VIEW:           'finance.provisions.view',
  PROVISIONS_MANAGE:         'finance.provisions.manage',
  POLICIES_VIEW:             'finance.policies.view',
  POLICIES_MANAGE:           'finance.policies.manage',
  SUBSEQUENT_EVENTS_VIEW:    'finance.subsequent_events.view',
  SUBSEQUENT_EVENTS_MANAGE:  'finance.subsequent_events.manage',
  ECL_VIEW:                  'finance.ecl.view',
  ECL_MANAGE:                'finance.ecl.manage',
  TREASURY_VIEW:      'treasury.view',
  TREASURY_MANAGE:    'treasury.manage',
  BANK_RECON_VIEW:    'bank_reconciliation.view',
  BANK_RECON_MANAGE:  'bank_reconciliation.manage',
  // Sprint S2 — seeded DB keys (20260601_s2_finance_permission_keys_seed.sql)
  /** @high-risk Post journal entries to GL (seeded canonical key) */
  POST_JOURNAL:       'finance.post_journal',
  /** @high-risk Approve payment vouchers within authority limits (seeded canonical key) */
  APPROVE_PAYMENT:    'finance.approve_payment',
  /** @high-risk Close fiscal periods and lock GL entries (seeded canonical key) */
  CLOSE_PERIOD:       'finance.close_period',
  /** Export reports to Excel / PDF / CSV (seeded canonical key) */
  REPORTS_EXPORT_ALL: 'reports.export',
  // Legacy keys
  VIEW_BALANCE_SHEET: 'view_balance_sheet',
  VIEW_CASH_FLOW:     'view_cash_flow',
  VIEW_GENERAL_LEDGER:'view_general_ledger',
  VIEW_PROFIT_LOSS:   'view_profit_loss',
  VIEW_TRIAL_BALANCE: 'view_trial_balance',
} as const;

// ── HR MODULE ─────────────────────────────────────────────────────────────────
export const HR = {
  ANALYTICS_VIEW:       'hr.analytics.view',
  ATTENDANCE_MANAGE:    'hr.attendance.manage',
  ATTENDANCE_VIEW:      'hr.attendance.view',
  COMPLIANCE_MANAGE:    'hr.compliance.manage',
  COMPLIANCE_VIEW:      'hr.compliance.view',
  DASHBOARD_VIEW:       'hr.dashboard.view',
  DOCUMENTS_MANAGE:     'hr.documents.manage',
  DOCUMENTS_VIEW:       'hr.documents.view',
  EMPLOYEES_APPROVE:    'hr.employees.approve',
  EMPLOYEES_CREATE:     'hr.employees.create',
  EMPLOYEES_EDIT:       'hr.employees.edit',
  EMPLOYEES_VIEW:       'hr.employees.view',
  EOSB_VIEW:            'hr.eosb.view',
  INSURANCE_VIEW:       'hr.insurance.view',
  LEAVE_APPROVE:        'hr.leave.approve',
  LEAVE_MANAGE:         'hr.leave.manage',
  LEAVE_VIEW:           'hr.leave.view',
  MANPOWER_VIEW:        'hr.manpower.view',
  OFFBOARDING_MANAGE:   'hr.offboarding.manage',
  ONBOARDING_MANAGE:    'hr.onboarding.manage',
  ORGCHART_VIEW:        'hr.orgchart.view',
  /** @high-risk Approve payroll batches */
  PAYROLL_APPROVE:      'hr.payroll.approve',
  /** @high-risk Process payroll */
  PAYROLL_MANAGE:       'hr.payroll.manage',
  /** @high-risk View payroll data */
  PAYROLL_VIEW:         'hr.payroll.view',
  PERFORMANCE_MANAGE:   'hr.performance.manage',
  PERFORMANCE_VIEW:     'hr.performance.view',
  RECRUITMENT_MANAGE:   'hr.recruitment.manage',
  RECRUITMENT_VIEW:     'hr.recruitment.view',
  SUCCESSION_VIEW:      'hr.succession.view',
  TRAINING_MANAGE:      'hr.training.manage',
  // Legacy
  CALCULATE_EOSB:       'calculate_eosb',
  MANAGE_PAYROLL:       'manage_payroll',
  VIEW_OWN_PAYSLIPS:    'view_own_payslips',
} as const;

// ── SELF-SERVICE (GENERAL MODULE) ─────────────────────────────────────────────
export const SELF = {
  ATTENDANCE_VIEW: 'self.attendance.view',
  DASHBOARD_VIEW:  'self.dashboard.view',
  DIRECTORY_VIEW:  'self.directory.view',
  EXPENSE_SUBMIT:  'self.expense.submit',
  LEAVE_SUBMIT:    'self.leave.submit',
  LEAVE_VIEW:      'self.leave.view',
  LOAN_REQUEST:    'self.loan.request',
  PAYSLIP_VIEW:    'self.payslip.view',
  PROFILE_EDIT:    'self.profile.edit',
  PROFILE_VIEW:    'self.profile.view',
  TIMESHEET_SUBMIT:'self.timesheet.submit',
  TRAINING_VIEW:   'self.training.view',
} as const;

// ── SALES MODULE ──────────────────────────────────────────────────────────────
export const SALES = {
  ANALYTICS_VIEW:      'sales.analytics.view',
  COMMISSIONS_MANAGE:  'sales.commissions.manage',
  COMMISSIONS_VIEW:    'sales.commissions.view',
  CPQ_MANAGE:          'sales.cpq.manage',
  CPQ_VIEW:            'sales.cpq.view',
  CRM_MANAGE:          'sales.crm.manage',
  CRM_VIEW:            'sales.crm.view',
  CUSTOMERS_MANAGE:    'sales.customers.manage',
  CUSTOMERS_VIEW:      'sales.customers.view',
  DASHBOARD_VIEW:      'sales.dashboard.view',
  JOBORDERS_VIEW:      'sales.joborders.view',
  ORDERS_MANAGE:       'sales.orders.manage',
  ORDERS_VIEW:         'sales.orders.view',
  PRICING_MANAGE:      'sales.pricing.manage',
  PRODUCTS_MANAGE:     'sales.products.manage',
  PRODUCTS_VIEW:       'sales.products.view',
  QUOTATIONS_APPROVE:  'sales.quotations.approve',
  QUOTATIONS_CREATE:   'sales.quotations.create',
  QUOTATIONS_EDIT:     'sales.quotations.edit',
  QUOTATIONS_VIEW:     'sales.quotations.view',
  TARGETS_MANAGE:      'sales.targets.manage',
  TARGETS_VIEW:        'sales.targets.view',
  WONDEALS_VIEW:       'sales.wondeals.view',
  ROI_VIEW:            'sales.roi.view',
  // Legacy
  APPROVE_QUOTATIONS:  'approve_quotations',
  MANAGE_CRM:          'manage_crm',
} as const;

// ── PROCUREMENT MODULE ────────────────────────────────────────────────────────
export const PROCUREMENT = {
  CONTRACTS_VIEW:       'procurement.contracts.view',
  DASHBOARD_VIEW:       'procurement.dashboard.view',
  IMPORTS_MANAGE:       'procurement.imports.manage',
  IMPORTS_VIEW:         'procurement.imports.view',
  /** @high-risk */
  PO_APPROVE:           'procurement.po.approve',
  PO_CREATE:            'procurement.po.create',
  PO_VIEW:              'procurement.po.view',
  REQUISITIONS_CREATE:  'procurement.requisitions.create',
  REQUISITIONS_VIEW:    'procurement.requisitions.view',
  RFQ_MANAGE:           'procurement.rfq.manage',
  RFQ_VIEW:             'procurement.rfq.view',
  SUPPLIERS_MANAGE:     'procurement.suppliers.manage',
  SUPPLIERS_VIEW:       'procurement.suppliers.view',
} as const;

// ── WAREHOUSE MODULE ──────────────────────────────────────────────────────────
export const WAREHOUSE = {
  ADJUSTMENTS_APPROVE: 'warehouse.adjustments.approve',
  ADJUSTMENTS_CREATE:  'warehouse.adjustments.create',
  BINS_MANAGE:         'warehouse.bins.manage',
  DASHBOARD_VIEW:      'warehouse.dashboard.view',
  GRN_APPROVE:         'warehouse.grn.approve',
  GRN_CREATE:          'warehouse.grn.create',
  GRN_VIEW:            'warehouse.grn.view',
  INVENTORY_MANAGE:    'warehouse.inventory.manage',
  INVENTORY_VIEW:      'warehouse.inventory.view',
  PICKING_MANAGE:      'warehouse.picking.manage',
  WMS_VIEW:            'warehouse.wms.view',
} as const;

// ── MANUFACTURING MODULE ──────────────────────────────────────────────────────
export const MANUFACTURING = {
  BOM_APPROVE:     'manufacturing.bom.approve',
  BOM_MANAGE:      'manufacturing.bom.manage',
  BOM_VIEW:        'manufacturing.bom.view',
  CAPACITY_VIEW:   'manufacturing.capacity.view',
  DASHBOARD_VIEW:  'manufacturing.dashboard.view',
  SHOPFLOOR_VIEW:  'manufacturing.shopfloor.view',
  WO_APPROVE:      'manufacturing.wo.approve',
  WO_CREATE:       'manufacturing.wo.create',
  WO_EXECUTE:      'manufacturing.wo.execute',
  WO_VIEW:         'manufacturing.wo.view',
} as const;

// ── PROJECTS MODULE ───────────────────────────────────────────────────────────
export const PROJECTS = {
  BILLING_MANAGE:        'projects.billing.manage',
  CHANGEORDERS_APPROVE:  'projects.changeorders.approve',
  CHANGEORDERS_CREATE:   'projects.changeorders.create',
  CREATE:                'projects.create',
  DASHBOARD_VIEW:        'projects.dashboard.view',
  MANAGE:                'projects.manage',
  PORTFOLIO_VIEW:        'projects.portfolio.view',
  TIMESHEETS_APPROVE:    'projects.timesheets.approve',
  VIEW:                  'projects.view',
} as const;

// ── QUALITY MODULE ────────────────────────────────────────────────────────────
export const QUALITY = {
  DASHBOARD_VIEW:       'quality.dashboard.view',
  INSPECTIONS_APPROVE:  'quality.inspections.approve',
  INSPECTIONS_CREATE:   'quality.inspections.create',
  INSPECTIONS_VIEW:     'quality.inspections.view',
  NCR_APPROVE:          'quality.ncr.approve',
  NCR_CREATE:           'quality.ncr.create',
  NCR_VIEW:             'quality.ncr.view',
  QMS_VIEW:             'quality.qms.view',
  QUARANTINE_MANAGE:    'quality.quarantine.manage',
  SPC_VIEW:                  'quality.spc.view',
  INCOMING_INSPECTION_VIEW:  'quality.incoming_inspection.view',
} as const;

// ── LOGISTICS MODULE ──────────────────────────────────────────────────────────
export const LOGISTICS = {
  CARRIERS_MANAGE:       'logistics.carriers.manage',
  DASHBOARD_VIEW:        'logistics.dashboard.view',
  INSTALLATIONS_MANAGE:  'logistics.installations.manage',
  INSTALLATIONS_VIEW:    'logistics.installations.view',
  ROUTES_MANAGE:         'logistics.routes.manage',
  ROUTES_VIEW:           'logistics.routes.view',
  SHIPMENTS_APPROVE:     'logistics.shipments.approve',
  SHIPMENTS_CREATE:      'logistics.shipments.create',
  SHIPMENTS_VIEW:        'logistics.shipments.view',
  SLA_VIEW:              'logistics.sla.view',
} as const;

// ── COMPLIANCE MODULE ─────────────────────────────────────────────────────────
export const COMPLIANCE = {
  AUDIT_VIEW:       'compliance.audit.view',
  CALENDAR_VIEW:    'compliance.calendar.view',
  DASHBOARD_VIEW:   'compliance.dashboard.view',
  GOSI_VIEW:        'compliance.gosi.view',
  GOVDOCS_MANAGE:   'compliance.govdocs.manage',
  GOVDOCS_VIEW:     'compliance.govdocs.view',
  NITAQAT_VIEW:     'compliance.nitaqat.view',
  REALESTATE_VIEW:  'compliance.realestate.view',
  RISK_VIEW:        'compliance.risk.view',
  VEHICLES_MANAGE:  'compliance.vehicles.manage',
  VEHICLES_VIEW:    'compliance.vehicles.view',
} as const;

// ── HSE MODULE ────────────────────────────────────────────────────────────────
export const HSE = {
  DASHBOARD_VIEW:    'hse.dashboard.view',
  HAZARDS_VIEW:      'hse.hazards.view',
  INCIDENTS_CREATE:  'hse.incidents.create',
  INCIDENTS_VIEW:    'hse.incidents.view',
  INSPECTIONS_MANAGE:'hse.inspections.manage',
  PPE_MANAGE:        'hse.ppe.manage',
} as const;

// ── ENGINEERING MODULE ────────────────────────────────────────────────────────
export const ENGINEERING = {
  BOM_MANAGE:           'engineering.bom.manage',
  CONFIGURATOR_MANAGE:  'engineering.configurator.manage',
  DASHBOARD_VIEW:       'engineering.dashboard.view',
  PLM_MANAGE:           'engineering.plm.manage',
} as const;

// ── EXECUTIVE MODULE ──────────────────────────────────────────────────────────
export const EXECUTIVE = {
  ANALYTICS_VIEW:    'executive.analytics.view',
  APPROVAL_FINAL:    'executive.approval.final',
  COMMERCIAL_VIEW:   'executive.commercial.view',
  CONSOLIDATED_VIEW: 'executive.consolidated.view',
  DASHBOARD_VIEW:    'executive.dashboard.view',
  FINANCE_VIEW:      'executive.finance.view',
  HR_VIEW:           'executive.hr.view',
  OPERATIONS_VIEW:   'executive.operations.view',
} as const;

// ── AFTERSALES MODULE ─────────────────────────────────────────────────────────
export const AFTERSALES = {
  RMA_MANAGE:       'aftersales.rma.manage',
  RMA_VIEW:         'aftersales.rma.view',
  TICKETS_MANAGE:   'aftersales.tickets.manage',
  TICKETS_VIEW:     'aftersales.tickets.view',
  WARRANTIES_VIEW:  'aftersales.warranties.view',
} as const;

// ── APPROVAL MODULE ───────────────────────────────────────────────────────────
export const APPROVAL = {
  EXPENSE_TEAM:   'approval.expense.team',
  LEAVE_TEAM:     'approval.leave.team',
  TIMESHEET_TEAM: 'approval.timesheet.team',
  // Approval matrix administration (Batch 6)
  /** View approval matrix rules and audit log */
  MATRIX_VIEW:      'approvals.view',
  /** Manage approval requests and process approvals */
  MATRIX_MANAGE:    'approvals.manage',
  /** Configure approval matrix rules and steps */
  MATRIX_CONFIGURE: 'approvals.configure',
  /** Override stuck or escalated approval requests */
  MATRIX_OVERRIDE:  'approvals.override',
  /** View approval matrix audit log */
  MATRIX_AUDIT:     'approvals.audit_view',
} as const;

// ── DOCUMENTS MODULE (Batch 7) ────────────────────────────────────────────────
export const DOCUMENTS = {
  CLAUSES_VIEW:      'documents.clauses.view',
  CLAUSES_MANAGE:    'documents.clauses.manage',
  TEMPLATES_VIEW:    'documents.templates.view',
  TEMPLATES_MANAGE:  'documents.templates.manage',
} as const;

// ── ONBOARDING MODULE ─────────────────────────────────────────────────────────
export const ONBOARDING = {
  /** View company onboarding runs and wizard status */
  VIEW:            'onboarding.view',
  /** Create and manage company onboarding runs */
  MANAGE:          'onboarding.manage',
  /** Approve go-live for newly onboarded companies */
  APPROVE_GO_LIVE: 'onboarding.approve_go_live',
} as const;

// ── MODULES MODULE ────────────────────────────────────────────────────────────
export const MODULES = {
  /** View module activation status per company */
  VIEW:      'modules.view',
  /** Enable or disable modules per company */
  MANAGE:    'modules.manage',
  /** Configure module-specific settings */
  CONFIGURE: 'modules.configure',
} as const;

// ── Convenience flat export ───────────────────────────────────────────────────
/** All permission keys grouped by module */
export const PERMISSIONS = {
  SYSTEM,
  ADMIN,
  FINANCE,
  HR,
  SELF,
  SALES,
  PROCUREMENT,
  WAREHOUSE,
  MANUFACTURING,
  PROJECTS,
  QUALITY,
  LOGISTICS,
  COMPLIANCE,
  HSE,
  ENGINEERING,
  EXECUTIVE,
  AFTERSALES,
  APPROVAL,
  MODULES,
  ONBOARDING,
  DOCUMENTS,
} as const;

/** Flat set of all permission keys — use for validation */
export const ALL_PERMISSION_KEYS = new Set<string>(
  Object.values(PERMISSIONS).flatMap(module => Object.values(module))
);
